import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { conversationsApi, usersApi, proposalsApi } from '../services/api';
import type { Message, User, Proposal } from '../types';
import { Avatar } from '../components/ui/Avatar';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { IconButton } from '../components/obros/IconButton';
import { Button } from '../components/ui/Button';
import { ProposalStatusChip } from '../components/obros/ProposalTile';
import { formatCurrency, formatRelativeTime } from '../utils/helpers';
import { useAuthStore } from '../stores/authStore';

type TimelineItem = { kind: 'message'; at: string; message: Message } | { kind: 'proposal'; at: string; proposal: Proposal };

// Calca ScreenChat de screens-chat.jsx (Claude Design): thread 1:1 con la propuesta del
// trabajo mostrada inline como burbuja, mezclada en la línea de tiempo por fecha.
export const ChatDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const isNew = id === 'new';
  const recipientId = Number(searchParams.get('recipientId'));
  const jobId = searchParams.get('jobId') ? Number(searchParams.get('jobId')) : undefined;

  const [conversationId, setConversationId] = useState<number | null>(isNew ? null : Number(id));
  const [conversationJobId, setConversationJobId] = useState<number | undefined>(jobId);
  const [jobTitle, setJobTitle] = useState<string | undefined>();
  const [otherUser, setOtherUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef<HTMLDivElement>(null);

  const loadProposal = async (job: number | undefined, otherId: number | undefined) => {
    if (!job || !otherId) return;
    // GET /proposals?job_id= sólo devuelve resultados para el cliente dueño del job; un worker
    // tiene que consultar sus propias propuestas y filtrar acá por job_id.
    const { data } =
      user?.user_type === 'worker' ? await proposalsApi.myProposals({}) : await proposalsApi.list({ job_id: job });
    const list: Proposal[] = data.data ?? data;
    const match = list.find((p) => p.job_id === job && (p.worker_id === otherId || p.worker_id === user?.id));
    setProposal(match || null);
  };

  useEffect(() => {
    const load = async () => {
      if (!isNew) {
        const { data } = await conversationsApi.get(Number(id));
        setConversationId(data.conversation.id);
        setConversationJobId(data.conversation.job_id);
        setJobTitle(data.conversation.job?.title);
        const other = data.conversation.client_id === user?.id ? data.conversation.worker : data.conversation.client;
        setOtherUser(other);
        setMessages(data.messages.data.reverse());
        await loadProposal(data.conversation.job_id, other?.id);
        setLoading(false);
        return;
      }

      const [{ data: recipient }, { data: existing }] = await Promise.all([usersApi.get(recipientId), conversationsApi.list()]);
      setOtherUser(recipient.user);

      const match = (existing.data ?? existing).find((c: any) => c.job_id === jobId && [c.client_id, c.worker_id].includes(recipientId));
      if (match) {
        const { data } = await conversationsApi.get(match.id);
        setConversationId(data.conversation.id);
        setJobTitle(data.conversation.job?.title);
        setMessages(data.messages.data.reverse());
      }
      await loadProposal(jobId, recipientId);
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, proposal]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    setText('');
    try {
      if (conversationId) {
        await conversationsApi.sendMessage(conversationId, content);
        const { data } = await conversationsApi.get(conversationId);
        setMessages(data.messages.data.reverse());
      } else {
        const { data } = await conversationsApi.create({ user_id: recipientId, job_id: jobId, message: content });
        setConversationId(data.id);
        navigate(`/messages/${data.id}`, { replace: true });
        const full = await conversationsApi.get(data.id);
        setMessages(full.data.messages.data.reverse());
      }
    } finally {
      setSending(false);
    }
  };

  const handleAccept = async () => {
    if (!proposal || !window.confirm('¿Aceptar esta propuesta?')) return;
    await proposalsApi.accept(proposal.id);
    const { data } = await proposalsApi.get(proposal.id);
    setProposal(data);
  };

  if (loading) return null;

  const isJobOwner = proposal ? user?.id !== proposal.worker_id : false;
  const timeline: TimelineItem[] = [
    ...messages.map((m): TimelineItem => ({ kind: 'message', at: m.created_at, message: m })),
    ...(proposal ? [{ kind: 'proposal', at: proposal.created_at, proposal } as TimelineItem] : []),
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-2 py-2 bg-surface border-b border-outlineVarWarm shrink-0">
        <IconButton icon="arrow_back" onClick={() => navigate('/messages')} />
        <Avatar src={otherUser?.avatar} name={otherUser?.full_name} size="md" />
        <p className="font-semibold text-onSurface ml-1">{otherUser?.full_name}</p>
      </div>

      {jobTitle && (
        <Link to={conversationJobId ? `/jobs/${conversationJobId}` : '#'} className="mx-4 mt-3 p-2 bg-cream2 rounded-xl flex items-center gap-2 shrink-0">
          <span className="w-7 h-7 rounded-lg bg-surface flex items-center justify-center text-primary-700 shrink-0">
            <MaterialIcon name="work" size={15} />
          </span>
          <span className="flex-1 min-w-0 text-xs font-medium text-onSurface truncate">{jobTitle}</span>
          <MaterialIcon name="chevron_right" size={16} className="text-onSurface-mute shrink-0" />
        </Link>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5 bg-surface-dim">
        {timeline.map((item) => {
          if (item.kind === 'proposal') {
            const mine = user?.id === item.proposal.worker_id;
            return (
              <div key="proposal" className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%] bg-surface border-[1.5px] border-primary-400 rounded-2xl p-3.5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <MaterialIcon name="description" size={15} className="text-primary-700" />
                    <span className="text-[11px] font-bold text-primary-700 uppercase tracking-wide">Propuesta</span>
                    <ProposalStatusChip status={item.proposal.status} />
                  </div>
                  <div className="flex gap-4 mb-2">
                    <div>
                      <p className="text-[10px] text-onSurface-mute font-semibold">PRECIO</p>
                      <p className="text-base font-extrabold text-onSurface">{formatCurrency(item.proposal.amount)}</p>
                    </div>
                    {item.proposal.estimated_days && (
                      <>
                        <div className="w-px bg-outlineVarWarm" />
                        <div>
                          <p className="text-[10px] text-onSurface-mute font-semibold">TIEMPO</p>
                          <p className="text-base font-extrabold text-onSurface">{item.proposal.estimated_days} días</p>
                        </div>
                      </>
                    )}
                  </div>
                  {item.proposal.cover_letter && <p className="text-[13px] text-onSurface-var mb-2.5">{item.proposal.cover_letter}</p>}
                  <div className="flex gap-1.5">
                    {isJobOwner && item.proposal.status === 'pending' && (
                      <Button variant="filled" size="sm" fullWidth onClick={handleAccept}>
                        Aceptar
                      </Button>
                    )}
                    <Link to={`/proposals/${item.proposal.id}`} className={isJobOwner && item.proposal.status === 'pending' ? 'shrink-0' : 'flex-1'}>
                      <Button variant="outlined" size="sm" fullWidth>
                        Ver detalle
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          }

          const msg = item.message;
          const isMine = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl ${
                  isMine ? 'bg-primary-500 text-white rounded-br-md' : 'bg-surface border border-outlineVarWarm text-onSurface rounded-bl-md'
                }`}
              >
                <p className="text-sm leading-relaxed">{msg.content}</p>
                <p className={`text-[10px] mt-1 text-right ${isMine ? 'text-white/70' : 'text-onSurface-mute'}`}>
                  {formatRelativeTime(msg.created_at)}
                  {isMine && ' ✓✓'}
                </p>
              </div>
            </div>
          );
        })}
        {timeline.length === 0 && <p className="text-center text-sm text-onSurface-mute pt-10">Empezá la conversación.</p>}
        <div ref={endRef} />
      </div>

      <form onSubmit={handleSend} className="flex items-center gap-2 px-3 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-surface border-t border-outlineVarWarm shrink-0">
        <div className="flex-1 h-10 rounded-full bg-surface-dim flex items-center px-4">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escribí un mensaje..."
            className="flex-1 bg-transparent outline-none text-sm text-onSurface placeholder-onSurface-mute"
          />
        </div>
        <button
          type="submit"
          disabled={!text.trim() || sending}
          className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center disabled:opacity-50 shrink-0"
        >
          <MaterialIcon name="send" size={18} />
        </button>
      </form>
    </div>
  );
};
